import { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, download, errMsg } from "@/lib/api";
import { BULAN, toISO } from "@/lib/format";
import { Card, Field, BigButton } from "@/components/admin/common";
import { AccountSelect } from "@/components/finance/shared";
import { DateInput } from "@/components/admin/DateInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const REPORTS = [
  ["laba-rugi", "Laporan Laba Rugi"], ["neraca", "Laporan Posisi Keuangan (Neraca)"], ["arus-kas", "Laporan Arus Kas"],
  ["buku-besar", "Buku Besar per Akun"], ["buku-kas", "Buku Kas / Bank"], ["neraca-saldo", "Neraca Saldo"],
  ["pph-final", "Rekap Peredaran Bruto & PPh Final"],
];
const STYLE = { section: "bg-soft font-bold", total: "font-bold border-t border-slate-300", grand: "font-extrabold border-t-2 border-ink bg-sand", note: "italic text-slate-500" };
const num = (v) => (v === null || v === undefined || v === "" ? "" : typeof v === "number" ? (v < 0 ? `(${Math.abs(Math.round(v)).toLocaleString("id-ID")})` : v === 0 ? "-" : Math.round(v).toLocaleString("id-ID")) : v);

const usePeriod = (mode, year, month, quarter, custom) => useMemo(() => {
  if (mode === "bulan") return [toISO(new Date(year, month, 1)), toISO(new Date(year, month + 1, 0))];
  if (mode === "triwulan") return [toISO(new Date(year, quarter * 3, 1)), toISO(new Date(year, quarter * 3 + 3, 0))];
  if (mode === "tahun") return [`${year}-01-01`, `${year}-12-31`];
  return [custom.start, custom.end];
}, [mode, year, month, quarter, custom]);

const ReportTable = ({ rep }) => (
  <Card className="overflow-x-auto" data-testid="report-table">
    <div className="p-4 border-b border-border">
      <p className="text-sm text-slate-500">{rep.company}{rep.npwp ? ` · NPWP ${rep.npwp}` : ""}</p>
      <p className="font-extrabold text-lg" data-testid="report-title">{rep.title}</p>
      <p className="text-sm text-slate-600">{rep.period}</p>
    </div>
    <table className="w-full text-sm">
      <thead><tr className="bg-ocean text-white">{rep.columns.map((c) => <th key={c.label} className={`px-3 py-2 font-semibold ${c.money ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr></thead>
      <tbody>
        {rep.rows.map((r, i) => (
          <tr key={i} className={`${STYLE[r.style] || "border-t border-border"}`}>
            {r.cells.map((c, j) => <td key={j} className={`px-3 py-2 ${rep.columns[j]?.money ? "text-right tabular-nums whitespace-nowrap" : ""}`}>{rep.columns[j]?.money ? num(c) : c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </Card>
);

export default function Reports({ accounts }) {
  const now = new Date();
  const [name, setName] = useState("laba-rugi");
  const [mode, setMode] = useState("bulan");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3));
  const [custom, setCustom] = useState({ start: `${now.getFullYear()}-01-01`, end: toISO(now) });
  const [account, setAccount] = useState("");
  const [rep, setRep] = useState(null);
  const [start, end] = usePeriod(name === "pph-final" ? "tahun" : mode, year, month, quarter, custom);
  const params = { start, end, account };
  const needsAcc = name === "buku-besar" || name === "buku-kas";

  useEffect(() => {
    api.get(`/admin/finance/reports/${name}`, { params: { start, end, account } }).then((r) => setRep(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [name, start, end, account]);

  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 4 + i);
  return (
    <div className="space-y-4" data-testid="finance-reports">
      <Card className="p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
        <Field label="Jenis laporan" className="sm:col-span-2">
          <Select value={name} onValueChange={(v) => { setName(v); setAccount(""); }}>
            <SelectTrigger className="h-12" data-testid="report-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{REPORTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        {name !== "pph-final" && (
          <Field label="Periode">
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="h-12" data-testid="report-period-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="bulan">Bulanan</SelectItem><SelectItem value="triwulan">Triwulan</SelectItem><SelectItem value="tahun">Tahunan</SelectItem><SelectItem value="kustom">Pilih tanggal</SelectItem></SelectContent>
            </Select>
          </Field>
        )}
        {(mode !== "kustom" || name === "pph-final") && (
          <Field label="Tahun">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-12" data-testid="report-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        {mode === "bulan" && name !== "pph-final" && (
          <Field label="Bulan">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-12" data-testid="report-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{BULAN.map((b, i) => <SelectItem key={b} value={String(i)}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        {mode === "triwulan" && name !== "pph-final" && (
          <Field label="Triwulan">
            <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}>
              <SelectTrigger className="h-12" data-testid="report-quarter-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["I (Jan–Mar)", "II (Apr–Jun)", "III (Jul–Sep)", "IV (Okt–Des)"].map((q, i) => <SelectItem key={q} value={String(i)}>Triwulan {q}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        {mode === "kustom" && name !== "pph-final" && <>
          <Field label="Dari"><DateInput required value={custom.start} onChange={(v) => v && setCustom({ ...custom, start: v })} testid="report-start-input" /></Field>
          <Field label="Sampai"><DateInput required value={custom.end} min={custom.start} onChange={(v) => v && setCustom({ ...custom, end: v })} testid="report-end-input" /></Field>
        </>}
        {needsAcc && (
          <Field label="Akun" className="sm:col-span-2">
            <AccountSelect accounts={name === "buku-kas" ? accounts.filter((a) => a.is_cash) : accounts} value={account} onChange={setAccount} placeholder="Semua akun" testid="report-account-select" />
          </Field>
        )}
        <BigButton onClick={() => download(`/admin/finance/reports/${name}/xlsx`, params)} data-testid="report-export-btn"><Download className="w-4 h-4" />Unduh Excel</BigButton>
      </Card>
      {rep && <ReportTable rep={rep} />}
    </div>
  );
}
